import { useEffect, useRef, useState, useCallback } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart'];

/**
 * Tracks user inactivity and triggers a warning + auto-logout.
 *
 * @param {number} timeoutMs  Total inactivity time before logout  (default 30 min) 
 * @param {number} warningMs  How long before logout to show warning (default 2 min)
 * @param {function} onTimeout  Called when the session expires
 *
 * @returns {{ showWarning, remaining, extendSession }}
 *   - showWarning    {boolean} — true when warning modal should be visible
 *   - remaining      {number}  — seconds left until auto-logout
 *   - extendSession  {function} — call to reset timers and dismiss warning
 */
export default function useSessionTimeout({
  timeoutMs = 30 * 60 * 1000,
  warningMs =  2 * 60 * 1000,
  onTimeout,
} = {}) {
  const [showWarning, setShowWarning] = useState(false);
  const [remaining,   setRemaining]   = useState(0);

  const timeoutTimer   = useRef(null);
  const warningTimer   = useRef(null);
  const countdownTimer = useRef(null);
  const isWarningShown = useRef(false);

  // Always call the latest version of onTimeout without re-registering effects
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);

  const clearAll = useCallback(() => {
    clearTimeout(timeoutTimer.current);
    clearTimeout(warningTimer.current);
    clearInterval(countdownTimer.current);
  }, []);

  const resetTimers = useCallback(() => {
    clearAll();
    isWarningShown.current = false;
    setShowWarning(false);
    setRemaining(0);

    // Show warning before logout
    warningTimer.current = setTimeout(() => {
      isWarningShown.current = true;
      setShowWarning(true);
      setRemaining(Math.floor(warningMs / 1000));

      countdownTimer.current = setInterval(() => {
        setRemaining(prev => Math.max(0, prev - 1));
      }, 1000);
    }, timeoutMs - warningMs);

    // Auto-logout
    timeoutTimer.current = setTimeout(() => {
      clearAll();
      onTimeoutRef.current?.();
    }, timeoutMs);
  }, [timeoutMs, warningMs, clearAll]);

  // Start on mount, clean up on unmount
  useEffect(() => {
    resetTimers();
    return clearAll;
  }, [resetTimers, clearAll]);

  // Reset on user activity — but NOT once the warning is showing
  // (only the explicit "Stay logged in" button can dismiss it)
  useEffect(() => {
    const handleActivity = () => {
      if (!isWarningShown.current) resetTimers();
    };
    ACTIVITY_EVENTS.forEach(e =>
      window.addEventListener(e, handleActivity, { passive: true })
    );
    return () => {
      ACTIVITY_EVENTS.forEach(e =>
        window.removeEventListener(e, handleActivity)
      );
    };
  }, [resetTimers]);

  return { showWarning, remaining, extendSession: resetTimers };
}
